import {present, property_of} from '@bitstillery/common/lib/utils'

import type {MithrilProp} from '@bitstillery/common/types/utils'

// Legacy prop store.
if (process.env.MSI_PACKAGE === 'discover') {
    window.prop = function <T>(init_value: T): MithrilProp<T> {
        const state = {value: init_value}
        const prop_fn = (new_value?: T): T => {
            if (new_value !== undefined) {
                state.value = new_value
            }

            return state.value
        }
        return prop_fn
    }
}

export function is_signal(obj: unknown): obj is {value: unknown} {
    return typeof obj === 'object' && obj !== null && 'value' in obj && !Array.isArray(obj)
}

/** A model reference as `modelref_adapter` resolves it: a signal, or an object and the key of its field. */
export type ModelRefResolved = {value: unknown} | [object | null | undefined, PropertyKey]

/** The `[ref, 'key']` form of a model reference; `ref` may be a legacy prop that answers the object. */
function is_model_path(model_ref: unknown[]): model_ref is [object | null | undefined, PropertyKey] {
    return model_ref.length === 2
}

/**
 * Adapts model references for multiple modes of operation:
 * 1. Direct Signal: obj.$key (Mithril state() accessor).
 * 2. Legacy window.prop: [obj, 'key'] where obj[key] is a function.
 * 3. Plain object: [obj, 'key'] where obj[key] is a plain value.
 * 4. Nested Signal: [obj, 'key'] where obj[key] is a Signal (obj[key].value).
 *
 * @param _model_ref - Array [ref, 'key'] or Signal (obj.$key).
 * @returns Object containing model_value and model_ref.
 */
export function modelref_adapter(_model_ref: unknown): {model_value: unknown; model_ref: ModelRefResolved | null} {
    if (_model_ref == null) {
        return {model_ref: null, model_value: null}
    }
    if (is_signal(_model_ref)) {
        return {model_ref: _model_ref, model_value: _model_ref.value}
    }
    // Plain primitive (string, number, boolean) - treat as read-only display value
    // Used when currency/model props receive raw values like "EUR" from was_sold_in, was_bought_in(), etc.
    if (typeof _model_ref !== 'object') {
        return {model_ref: null, model_value: _model_ref}
    }

    if (!Array.isArray(_model_ref)) {
        throw new TypeError('model reference must be an array or Signal')
    }
    if (!is_model_path(_model_ref)) {
        throw new Error(`modelref format [ref, 'key'] incorrect: ${String(_model_ref)}`)
    }
    let model_ref: [object | null | undefined, PropertyKey], model_value: unknown

    // No reference (yet); abort check
    if (!_model_ref[0]) {
        return {model_ref: null, model_value: null}
    }

    if (typeof _model_ref[0] === 'function') {
        // Path reference to a prop; e.g. [@lead, 'country_code']
        const resolved: unknown = Reflect.apply(_model_ref[0], undefined, [])
        model_ref = [typeof resolved === 'object' || typeof resolved === 'function' ? resolved : null, _model_ref[1]]
    } else {
        model_ref = [_model_ref[0], _model_ref[1]]
    }

    // Resolved reference is null (e.g. supplier not loaded yet)
    if (model_ref[0] == null) {
        return {model_ref: null, model_value: null}
    }

    // Simple key reference to a nested prop
    if (typeof model_ref[1] === 'string' && model_ref[1].includes('.')) {
        // Path reference to a nested prop
        model_value = model_ref[1].split('.').reduce((prev: unknown, key: string): unknown => {
            const path_prop = present(property_of(prev, key), `model path ${key}`)
            if (typeof path_prop !== 'function') {
                throw new TypeError(`model path ${key} is not a prop`)
            }
            return Reflect.apply(path_prop, undefined, [])
        }, model_ref[0])
    } else {
        const targetref = property_of(model_ref[0], model_ref[1])
        if (typeof targetref === 'function') {
            model_value = Reflect.apply(targetref, undefined, [])
        } else if (is_signal(targetref)) {
            model_value = targetref.value
        } else {
            model_value = targetref
        }
    }

    return {model_ref, model_value}
}

/**
 * Wrapper for data assignments in components that are being
 * used with window.props and the Proxy store. Once these are
 * phased out, this wrapper can be removed.
 * @param modelref - [obj, key] array or Signal
 * @param modelvalue - value to assign
 */
export function modelref_assign(modelref: ModelRefResolved | null | undefined, modelvalue: unknown) {
    if (modelref == null) {
        return
    }
    if (is_signal(modelref)) {
        modelref.value = modelvalue
        return
    }
    const [ref, key] = modelref
    if (ref == null) {
        return
    }
    const target = property_of(ref, key)
    if (typeof target === 'function') {
        Reflect.apply(target, undefined, [modelvalue])
    } else if (is_signal(target)) {
        target.value = modelvalue
    } else {
        Reflect.set(ref, key, modelvalue)
    }
}

// Re-export Mithril Store as our Store
export {Store} from '@bitstillery/mithril'

export class Registry<T, C> {
    factory: (context: C) => T
    private instances = new Map<string | number, T>()

    constructor(factory: (context: C) => T) {
        this.factory = factory
    }

    get(key: string | number) {
        return this.instances.get(key)
    }

    /**
     * Get the instance for the given key, creating it from `context` when there is none yet.
     */
    get_or_create(key: string | number, context: C): T {
        const existing = this.instances.get(key)
        if (existing !== undefined) {
            return existing
        }
        const created = this.factory(context)
        this.instances.set(key, created)
        return created
    }

    /**
     * Check if an instance exists for the given key.
     */
    has(key: string | number): boolean {
        return this.instances.has(key)
    }

    /**
     * Remove an instance for the given key.
     * Useful for cleanup when a component unmounts or data is removed.
     */
    delete(key: string | number): boolean {
        return this.instances.delete(key)
    }

    /**
     * Clear all instances.
     * Useful for cleanup on navigation or state reset.
     */
    clear() {
        this.instances.clear()
    }

    /**
     * Get the number of registered instances.
     */
    get size(): number {
        return this.instances.size
    }

    /**
     * Get all registered keys.
     */
    keys(): IterableIterator<string | number> {
        return this.instances.keys()
    }
}

// Watch() function is now imported from @bitstillery/mithril
// All watch calls have been updated to use the new signal-based API
